import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentItems } from '../data/assessmentQuestions';
import { Button } from './ui/button';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Relatorio, { C } from './Relatorio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Componente principal ──────────────────────────────────────────────────────
const InitialAssessment = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [step, setStep]           = useState('loading'); // loading|welcome|quiz|intencao|analyzing|result|cooldown
  const [nome, setNome]           = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers]     = useState({});
  const [intencao, setIntencao]   = useState('');
  const [result, setResult]       = useState(null);
  const [isRedo, setIsRedo]       = useState(false);
  const [cooldown, setCooldown]   = useState(null);
  const [isSaving, setIsSaving]   = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/auth/assessment/status`, { withCredentials: true });
        const data = res.data;
        if (!data.can_reassess) {
          setCooldown(data);
          setStep('cooldown');
        } else {
          setIsRedo(data.numero_aplicacao > 0);
          setStep('welcome');
        }
      } catch {
        setStep('welcome');
      }
    };
    checkStatus();
    // Pre-fill name from auth user
    if (authUser?.name) setNome(authUser.name.split(' ')[0]);
  }, [authUser]);

  const currentItem    = assessmentItems[currentIndex];
  const selectedScore  = answers[currentItem?.id];
  const answeredCount  = Object.keys(answers).length;
  const progressPct    = Math.round((answeredCount / assessmentItems.length) * 100);

  const handleSelectScore = (score) => {
    setAnswers(prev => ({ ...prev, [currentItem.id]: score }));
  };

  const handleNext = () => {
    if (currentIndex < assessmentItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep('intencao');
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const runAnalysis = async () => {
    setStep('analyzing');
    try {
      const res = await axios.post(
        `${API}/auth/analyze-assessment`,
        {
          itens: answers,
          intencao: intencao.trim(),
          nome: nome.trim(),
          tipo: isRedo ? 'reassessment' : 'inicial',
        },
        { withCredentials: true }
      );
      setResult(res.data);
    } catch (err) {
      console.log('❌ Assessment analysis error:', err);
      // Fallback completo com dados estruturados E domínios calculados
      // Calcular domínios básicos das respostas do usuário
      const calcularDominios = () => {
        const DOMAIN_MAP = {
          autoconsciencia: [1, 2, 3, 4],
          autorregulacao: [5, 6, 7, 8],
          expressao: [9, 10, 11, 12],
          empatia: [13, 14, 15, 16],
          conflito: [17, 18, 19, 20],
          sistema: [21, 22, 23, 24, 25, 26, 27, 28],
        };
        
        const dominios = {};
        for (const [dom, items] of Object.entries(DOMAIN_MAP)) {
          const valores = items.map(i => answers[i]).filter(v => v !== undefined);
          const media = valores.length > 0 ? valores.reduce((a, b) => a + b, 0) / valores.length : 2.5;
          dominios[dom] = { media: Number(media.toFixed(2)) };
        }
        return dominios;
      };
      
      const dominiosCalculados = calcularDominios();
      const mediasArray = Object.values(dominiosCalculados).map(d => d.media);
      const mediaGlobal = mediasArray.reduce((a, b) => a + b, 0) / mediasArray.length;
      const scoreGeral = Math.round(((mediaGlobal - 1) / 3) * 100);
      
      // Ilha recomendada = domínio mais fraco
      const DOMAIN_TO_ISLAND = {
        autoconsciencia: 'autogestao',
        autorregulacao: 'autogestao',
        expressao: 'comunicacao',
        empatia: 'comunicacao',
        conflito: 'conflitos',
        sistema: 'ambientes',
      };
      const dominioMaisFraco = Object.entries(dominiosCalculados)
        .sort(([,a], [,b]) => a.media - b.media)[0][0];
      const ilhaRecomendada = DOMAIN_TO_ISLAND[dominioMaisFraco] || 'autogestao';
      
      setResult({
        perfil_geral: 'Há algo muito claro nos seus dados: você já tem consciência de si mesma. Isso é raro e valioso — e é exatamente o que torna essa jornada possível.',
        forcas: [
          { habilidade: 'Autoaceitação', nivel: 'Plenamente desenvolvida', descricao: 'Você demonstra uma capacidade sólida de reconhecer suas qualidades e limitações sem julgamento severo.' },
          { habilidade: 'Consciência das necessidades', nivel: 'Disponível', descricao: 'Você identifica com clareza o que é importante para você em diferentes situações.' }
        ],
        desenvolvimento: [
          { habilidade: 'Paciência — pausar quando necessário', nivel: 'Em desenvolvimento', descricao: 'Há espaço para fortalecer a capacidade de dar um passo atrás antes de reagir.' },
          { habilidade: 'Flexibilidade nas relações', nivel: 'Em desenvolvimento inicial', descricao: 'Adaptar-se a diferentes contextos relacionais ainda é um desafio.' }
        ],
        tensoes: [
          { polo_forte: 'Autoaceitação', polo_fragil: 'Flexibilidade nas relações', descricao: 'Você se aceita como é, mas pode ter dificuldade em se adaptar quando os outros não correspondem às suas expectativas.' }
        ],
        plano: [
          { titulo: 'Começar por dentro', habilidades: ['Paciência', 'Auto-responsabilização'], descricao: 'O ponto de partida mais fértil é aquele onde pequenos movimentos criam grandes ondas. Desenvolver a pausa consciente antes de agir.' },
          { titulo: 'Expandir a escuta', habilidades: ['Empatia genuína', 'Abertura ao feedback'], descricao: 'A qualidade das suas relações cresce na medida em que você se torna mais presente para o outro e genuinamente curiosa sobre suas perspectivas.' },
          { titulo: 'Agir no sistema', habilidades: ['Flexibilidade', 'Transformar conflito'], descricao: 'O que você constrói internamente vai se refletir nos ambientes que você habita. Praticar adaptação em contextos relacionais diversos.' },
        ],
        dominios: dominiosCalculados,
        score_geral: scoreGeral,
        ilha_recomendada: ilhaRecomendada,
        numero_aplicacao: 1,
      });
    }
    setStep('result');
  };

  const handleFinish = async () => {
    if (!result) return;
    setIsSaving(true);
    const payload = {
      perfil_relacional: result,
      dominios: result.dominios,
      score_geral: result.score_geral,
      ilha_recomendada: result.ilha_recomendada,
      numero_aplicacao: result.numero_aplicacao,
      // compat fields for complete-assessment
      competencias: result.dominios,
      simby_analysis: result.perfil_geral,
    };
    try {
      if (isRedo) {
        await axios.post(`${API}/auth/redo-assessment`, payload, { withCredentials: true });
        navigate('/progress');
      } else if (onComplete) {
        await onComplete(payload);
        navigate('/dashboard');
      } else {
        await axios.post(`${API}/auth/complete-assessment`, payload, { withCredentials: true });
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('complete/redo-assessment error:', err);
      alert('Erro ao salvar assessment: ' + (err.response?.data?.detail || err.message || 'Erro desconhecido. Por favor, tente novamente.'));
    } finally {
      setIsSaving(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: C.medio }} />
      </div>
    );
  }

  // ── Cooldown ───────────────────────────────────────────────────────────────
  if (step === 'cooldown') {
    const lastDate = cooldown?.last_assessment_date
      ? new Date(cooldown.last_assessment_date).toLocaleDateString('pt-BR')
      : null;
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="max-w-md w-full p-8 text-center bg-white rounded-2xl shadow-lg border" style={{ borderColor: C.borda }}>
          <p className="text-5xl font-bold mb-2" style={{ color: C.medio }}>{cooldown?.days_remaining}</p>
          <p className="text-gray-600 mb-1 text-lg">dias para o próximo assessment</p>
          {lastDate && <p className="text-sm text-gray-400 mb-6">Última avaliação: {lastDate}</p>}
          <p className="text-sm text-gray-500 mb-6">
            O reassessment fica disponível 60 dias após a última aplicação — tempo para que mudanças reais apareçam nos dados.
          </p>
          <Button className="w-full text-white" style={{ backgroundColor: C.medio }} onClick={() => navigate('/dashboard')}>
            Ir para o Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // ── Welcome ────────────────────────────────────────────────────────────────
  if (step === 'welcome') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="max-w-lg w-full p-8 bg-white rounded-2xl shadow-lg border" style={{ borderColor: C.borda }}>
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo-simby-full.png" alt="Simby" style={{ height: 32, width: 'auto' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ color: C.escuro, fontFamily: 'Georgia, serif' }}>
            Seu Perfil Relacional
          </h1>
          <p className="text-gray-600 mb-2">
            São 28 afirmações sobre habilidades relacionais. Não há resposta certa ou errada — escolha o que mais reflete sua experiência atual.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Leva cerca de 10 minutos. Ao final, você recebe um relatório com seu perfil de inteligência relacional.
          </p>
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2" style={{ color: C.primario }}>
              Como você quer ser chamada no relatório?
            </label>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Seu nome"
              className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: C.borda, focusRingColor: C.medio }}
            />
          </div>
          <Button
            disabled={!nome.trim()}
            onClick={() => setStep('quiz')}
            size="lg"
            className="w-full text-white text-base py-6"
            style={{ backgroundColor: C.medio }}
          >
            Vamos começar <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Analyzing ─────────────────────────────────────────────────────────────
  if (step === 'analyzing') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="max-w-sm w-full p-8 text-center bg-white rounded-2xl shadow-lg border" style={{ borderColor: C.borda }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-6" style={{ borderColor: C.medio }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: C.escuro }}>Analisando seu perfil de inteligência relacional…</h2>
          <p className="text-sm text-gray-500">Isso leva alguns segundos.</p>
          <div className="w-full rounded-full h-1.5 mt-6" style={{ backgroundColor: C.borda }}>
            <div className="h-1.5 rounded-full animate-pulse" style={{ width: '65%', backgroundColor: C.medio }} />
          </div>
        </div>
      </div>
    );
  }

  // ── Result ────────────────────────────────────────────────────────────────
  if (step === 'result' && result) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="max-w-2xl mx-auto">
          <Relatorio
            result={result}
            nome={nome}
            intencao={intencao}
            onContinue={handleFinish}
            isSaving={isSaving}
          />
        </div>
      </div>
    );
  }

  // ── Q29 — Pergunta aberta ──────────────────────────────────────────────────
  if (step === 'intencao') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
        <div className="max-w-lg w-full p-8 bg-white rounded-2xl shadow-lg border" style={{ borderColor: C.borda }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: C.cinza }}>
            Pergunta aberta
          </p>
          <h2 className="text-xl font-bold mb-2" style={{ color: C.escuro, fontFamily: 'Georgia, serif' }}>
            O que você quer transformar na sua inteligência relacional?
          </h2>
          <p className="text-sm text-gray-500 mb-5">Descreva com suas palavras.</p>
          <textarea
            value={intencao}
            onChange={e => setIntencao(e.target.value)}
            placeholder="Pode escrever livremente — o que vier..."
            rows={5}
            className="w-full border rounded-xl px-4 py-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 mb-5"
            style={{ borderColor: C.borda }}
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setCurrentIndex(assessmentItems.length - 1); setStep('quiz'); }}
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
            <Button
              disabled={!intencao.trim()}
              onClick={runAnalysis}
              className="flex-1 text-white"
              style={{ backgroundColor: C.medio }}
            >
              Ver meu perfil <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Quiz ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pt-8 pb-40 md:pb-28 px-4" style={{ background: `linear-gradient(to bottom, ${C.cremelc}, white)` }}>
      <div className="max-w-2xl mx-auto">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-medium" style={{ color: C.primario }}>
              {currentItem?.label}
            </span>
            <span className="text-gray-400">{currentIndex + 1} / {assessmentItems.length}</span>
          </div>
          <div className="w-full rounded-full h-1.5" style={{ backgroundColor: C.borda }}>
            <div
              className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%`, backgroundColor: C.medio }}
            />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white rounded-2xl p-6 mb-5 shadow-sm border" style={{ borderColor: C.borda }}>
          {currentItem?.subtitle && (
            <p className="text-xs mb-4 font-medium uppercase tracking-wide" style={{ color: C.cinza }}>
              {currentItem.subtitle}
            </p>
          )}
          <p className="font-bold mb-5 text-lg leading-snug" style={{ color: C.escuro, fontFamily: 'Georgia, serif' }}>
            {currentItem?.label}
          </p>
          <div className="space-y-3">
            {currentItem?.options.map((option) => {
              const isSelected = selectedScore === option.score;
              return (
                <button
                  key={option.score}
                  onClick={() => handleSelectScore(option.score)}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-200 border-2 ${
                    isSelected ? 'shadow-sm' : 'bg-white hover:bg-gray-50'
                  }`}
                  style={{
                    borderColor: isSelected ? C.medio : C.borda,
                    backgroundColor: isSelected ? C.cremelc : undefined,
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-gray-800 leading-relaxed flex-1 text-sm">{option.text}</p>
                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: C.medio }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

      </div>

      {/* Navigation — fixed above mobile bottom navbar (h-14 = 56px) on mobile, at bottom on desktop */}
      <div className="fixed bottom-14 md:bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-lg px-4 py-3">
        <div className="max-w-2xl mx-auto flex gap-3">
          <Button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
          </Button>
          <Button
            onClick={handleNext}
            disabled={selectedScore === undefined}
            size="lg"
            className="flex-1 text-white"
            style={{ backgroundColor: C.medio }}
          >
            {currentIndex < assessmentItems.length - 1 ? 'Próxima' : 'Concluir'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InitialAssessment;
